import { useEffect, useRef, useState, type ReactNode } from "react";
import { FREE_TURNS, FIRST_DREAM_AT, PATRON_TURNS_PER_MONTH, type PatronView } from "../shared/patron";
import { api } from "./api";
import { Unravel, type Skin } from "./common";
import { Credit, Sheet } from "./trust";
import { introSeen } from "./v2/intro";

/**
 * How Weftwake plays: seven short steps, each with a small working mock of the app that acts itself out on a loop (write
 * a line and see it kept; cut in; look closer, closer again, and pocket a name; /ask; the two pens, Ink and Thread;
 * bring a thread back; chapters and the storyteller). Not shown on its own: the home screen's "New here?" invites it, "how to play" links and the typed "how
 * to play" open it, over whatever is open (a story underneath keeps its place and its unsent line). Typing "help"
 * opens the short list instead (ShortList, below). Arrow keys and swipes move between steps; under reduced motion each
 * example shows its final frame. The demos speak the open theme's words ("you ⟩", "‸", ">"; unwoven, struck, dropped).
 */

const KEY = "loom:onboarded";
export function onboarded(): boolean { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } }
export function markOnboarded() { try { localStorage.setItem(KEY, "1"); } catch { /* shown again next visit */ } }

const reducedMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Milliseconds into a looping timeline of `total` ms (the final frame under reduced motion). */
function useTimeline(total: number): number {
  const [t, setT] = useState(() => (reducedMotion() ? total - 1 : 0));
  useEffect(() => {
    if (reducedMotion()) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => { setT((now - start) % total); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total]);
  return t;
}
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
/** The first `p` share of `text`, cut at a character. */
const part = (text: string, p: number) => text.slice(0, Math.round(clamp01(p) * text.length));

/** How each theme marks the player's lines, the storyteller's asides and a cut. */
const WORDS: Record<Skin, { you: string; toTeller: string; teller: string; cut(n: number): string }> = {
  nitrate: { you: "you ·", toTeller: "to the projectionist ·", teller: "projectionist ·", cut: (n) => `${n} words cut from the reel` },
  cyanotype: { you: "you ·", toTeller: "to the darkroom ·", teller: "darkroom ·", cut: (n) => `${n} words left unexposed` },
  edition: { you: "your headline ·", toTeller: "to the editor ·", teller: "editor's note ·", cut: (n) => `${n} words cut from the column` },
  manuscript: { you: "‸", toTeller: "to the printer:", teller: "the printer:", cut: (n) => `${n} words struck from the press` },
  phosphor: { you: ">", toTeller: ">", teller: "# narrator:", cut: (n) => `^C [${n} words dropped]` },
};

function Line({ text, caret, sent }: { text: string; caret?: boolean; sent?: boolean }) {
  return (
    <div className={`lm-ob-line ${sent ? "lm-ob-line-sent" : ""}`}>
      <span className="lm-ob-line-text">{text || <span className="lm-ob-line-ph">What do you do?</span>}{caret && <span className="lm-caret" aria-hidden="true" />}</span>
      <span className="lm-ob-send" aria-hidden="true">↵</span>
    </div>
  );
}
const You = ({ w, children, mark }: { w: (typeof WORDS)[Skin]; children: ReactNode; mark?: string }) => (
  <p className="lm-ob-you"><span>{mark ?? w.you}</span> {children}</p>
);

// I · Write what you do (and what's true about you) -------------------------------------------------------------
function DemoWrite({ w }: { w: (typeof WORDS)[Skin] }) {
  const you = "I miss the ferry. I carry my father's watch.";
  const reply = "The ferry is a low shape on the grey water, its wake closing behind it. The watch ticks against your ribs, a little slow, the way it always ran.";
  const t = useTimeline(12000);
  const sent = t > 3200;
  return (
    <>
      {sent && (
        <You w={w}>
          {you}
          {t > 7600 && <> <span className={`lm-ob-kept ${t > 8400 && t < 8900 ? "lm-ob-tap" : ""}`}>kept</span></>}
          {t > 8900 && <span className="lm-ob-kept-list"> Kept as true: you carry your father's watch.</span>}
        </You>
      )}
      {sent && <p className="lm-ob-prose">{part(reply, (t - 3500) / 3800)}{t < 7300 && <span className="lm-caret" aria-hidden="true" />}</p>}
      <Line text={sent ? "" : part(you, t / 2900)} caret={!sent} sent={t > 3000 && t < 3400} />
    </>
  );
}

// II · Cut in -----------------------------------------------------------------------------------------------------
function DemoCut({ w }: { w: (typeof WORDS)[Skin] }) {
  const kept = "He doesn't ask for your fare. He asks";
  const rest = "whether you remember the harbour you sailed from, and the name of the woman who waved.";
  const you = "I hold out the fare anyway.";
  const t = useTimeline(12500);
  const writing = t < 2600;
  const typing = t >= 2600 && t < 5200;
  const cut = t >= 5200;
  return (
    <>
      <p className="lm-ob-prose">
        {writing ? part(kept, t / 2600) : kept}
        {writing && <span className="lm-caret" aria-hidden="true" />}
        {cut && <>—{t < 9800 ? <> <Unravel key="u" text={rest} /></> : null}{t > 7400 && <span className="lm-unwoven"> {w.cut(16)}</span>}</>}
      </p>
      {typing && <p className="lm-ob-note">you're typing: the narrator waits</p>}
      {cut && <You w={w}>{you}</You>}
      <Line text={typing ? part(you, (t - 2700) / 2000) : ""} caret={typing} sent={t > 5000 && t < 5300} />
    </>
  );
}

// III · Look closer, and pocket a name -----------------------------------------------------------------------------
function DemoLook() {
  const t = useTimeline(14000);
  const open1 = t > 1600;
  const open2 = t > 4800;
  const older = t > 8000;
  const pocketed = t > 10600;
  return (
    <>
      <p className="lm-ob-prose">The <span className={`lm-ob-dry ${older && t < 8600 ? "lm-ob-tap" : ""}`}>harbour</span> is quiet. On the empty bench lies a <span className={`lm-ob-name ${t > 900 && t < 1600 ? "lm-ob-tap" : ""} ${open1 ? "lm-ob-name-open" : ""}`}>ferryman's cap</span>, still damp.</p>
      {open1 && (
        <div className="lm-ob-card">
          <p className="lm-ob-card-h">ferryman's cap</p>
          <p>Seawater beads on the brim, though the boat left an hour ago. Tucked inside the <span className={`lm-ob-name ${t > 4100 && t < 4800 ? "lm-ob-tap" : ""} ${open2 ? "lm-ob-name-open" : ""}`}>band</span>, a folded slip.</p>
          {open2 && (
            <div className="lm-ob-card lm-ob-card-inner">
              <p className="lm-ob-card-h">inside the band</p>
              <p>A folded slip, a name in faded ink. It takes you a moment to recognise it as yours.</p>
            </div>
          )}
          {open1 && <p className="lm-ob-acts"><span className={pocketed ? "lm-ob-held" : t > 9800 ? "lm-ob-tap" : ""}>{pocketed ? "in your pocket" : "Pocket it"}</span> <span>Write with it</span></p>}
        </div>
      )}
      {older && !pocketed && <p className="lm-ob-note">“harbour” looks plain now; tap it and it still opens</p>}
      {pocketed && <p className="lm-ob-pocket"><span className="lm-ob-pocket-h">pocket</span> <span className="lm-ob-chip">◦ your father's watch</span> <span className="lm-ob-chip lm-ob-chip-word">ferryman's cap</span></p>}
    </>
  );
}

// V · Two pens: Ink and Thread ------------------------------------------------------------------------------------
function DemoPens({ w }: { w: (typeof WORDS)[Skin] }) {
  const line = "The ferryman is my cousin, and he owes me a favour.";
  const reply = "He looks up, and it is Pavel, with your aunt's heavy eyebrows. “I know what I owe you,” he says, and lifts the chain.";
  const t = useTimeline(15000);
  const thread = t > 1300 && t < 3900;
  const sent = t > 3900;
  const left = sent ? 2 : 3;
  return (
    <>
      <p className="lm-ob-pens" aria-hidden="true">
        <span className={`lm-ob-pen ${thread ? "" : "lm-ob-pen-on"}`}>Ink</span>
        <span className={`lm-ob-pen lm-ob-pen-thread ${thread ? "lm-ob-pen-on" : ""} ${t > 700 && t < 1300 ? "lm-ob-tap" : ""}`}>
          Thread <span className="v2-knots">{[0, 1, 2].map((k) => <span key={k} className={`v2-knot${k < left ? " v2-knot-on" : ""}`} />)}</span>
        </span>
        {thread && <span className="v2-pen-hint">Write what is true now.</span>}
      </p>
      {sent && <You w={w}><span className="v2-thread">{line}</span></You>}
      {sent && <p className="lm-ob-prose">{part(reply, (t - 4200) / 3600)}{t < 7800 && <span className="lm-caret" aria-hidden="true" />}</p>}
      {t > 8300 && (
        <aside className="v2-pull lm-ob-pull">
          <span className="v2-pull-h">the world pulls back</span>
          <p>Pavel's price: you carry his sealed parcel across, and if customs stop you, it's yours.</p>
        </aside>
      )}
      {t > 11000 && <p className="lm-ob-note">three threads a chapter · one that would undo the past is refused, and given back</p>}
      <div className={thread ? "lm-ob-line-gold" : ""}>
        <Line text={thread ? part(line, (t - 1500) / 2100) : ""} caret={thread} sent={t > 3700 && t < 4100} />
      </div>
    </>
  );
}

// IV · The world answers back -------------------------------------------------------------------------------------
function DemoAsk({ w }: { w: (typeof WORDS)[Skin] }) {
  const ask = "/ask Is the ferryman lying?";
  const reply = "He meets your eye a beat too long. Then his thumb rubs a coin he isn't holding.";
  const t = useTimeline(12000);
  const menu = t > 600 && t < 2200;
  const sent = t > 4200;
  return (
    <>
      {sent && <You w={w}>{ask}</You>}
      {sent && <p className="lm-ob-prose">{part(reply, (t - 4500) / 3400)}{t < 7900 && <span className="lm-caret" aria-hidden="true" />}</p>}
      {t > 8600 && <p className="lm-ob-note">/trouble makes something happen · /clock until dawn sets a clock of your own · /flashback makes something you did earlier true · type / to see them all</p>}
      {menu && (
        <ul className="lm-ob-verbs" aria-hidden="true">
          <li className="lm-ob-verb-on"><strong>/ask</strong> ask the world a question</li>
          <li><strong>/elsewhere</strong> go somewhere else, or later</li>
          <li><strong>/trouble</strong> something happens now</li>
          <li><strong>/clock</strong> a clock of your own</li>
          <li><strong>/flashback</strong> something you did earlier</li>
          <li><strong>/weave</strong> make something true (the Thread pen)</li>
        </ul>
      )}
      <Line text={sent ? "" : t < 2200 ? (t > 400 ? "/" : "") : part(ask, (t - 2000) / 1900)} caret={!sent} sent={t > 4000 && t < 4400} />
    </>
  );
}

// VI · Loose ends come back ---------------------------------------------------------------------------------------
function DemoThreads({ w }: { w: (typeof WORDS)[Skin] }) {
  const t = useTimeline(12000);
  const tapped = t > 2600;
  const sent = t > 4600;
  const reply = "A boy runs down the jetty with a luggage tag. It's yours, and someone has written a new address on the back.";
  return (
    <>
      <ul className="lm-ob-threads-list" aria-label="Threads">
        <li className={`lm-ob-quiet ${t > 2000 && t < 2600 ? "lm-ob-tap" : ""}`}>↺ The suitcase on the ferry <span>quiet for 6 turns</span></li>
        <li>↺ The woman in green <span>in play</span></li>
      </ul>
      {sent && <You w={w}>↺ The suitcase on the ferry</You>}
      {sent && <p className="lm-ob-prose">{part(reply, (t - 4900) / 3600)}{t < 8500 && <span className="lm-caret" aria-hidden="true" />}</p>}
      {t > 9000 && <p className="lm-ob-note">bringing a thread back is a line like any other: one turn</p>}
      <Line text={tapped && !sent ? "↺ The suitcase on the ferry " : ""} caret={tapped && !sent} sent={t > 4400 && t < 4800} />
    </>
  );
}

// VII · Chapters, the journal and the storyteller -----------------------------------------------------------------
function DemoChapters({ w }: { w: (typeof WORDS)[Skin] }) {
  const t = useTimeline(12000);
  const ended = t > 2600;
  const aside = t > 6400;
  return (
    <>
      <p className="lm-ob-chapter"><span>Chapter I</span> Will you reach Low Anchorage before the tide turns?</p>
      {ended && (
        <div className="lm-ob-end">
          <p className="lm-ob-card-h">Chapter I ends</p>
          <p><strong>Answered, at a price.</strong> The price: your father's watch stopped at 4:40.</p>
        </div>
      )}
      {aside && <You w={w} mark={w.toTeller}>slower, please</You>}
      {t > 7600 && <p className="lm-ob-prose lm-ob-aside"><span>{w.teller}</span> Slower it is.</p>}
    </>
  );
}

type W = (typeof WORDS)[Skin];
const PENS_STEP = "Two pens: Ink and Thread";
const STEPS: { h: string; p: ReactNode; Demo: (props: { w: W }) => ReactNode }[] = [
  { h: "Write what you do", p: <>Write what your character does, in a word or a page, and press Enter. <strong>Each line you send is one turn.</strong> You can also say what's true about you: write “I carry my father's watch,” and from then on you do.</>, Demo: DemoWrite },
  { h: "Cut in whenever you like", p: <>The narrator writes at reading pace, and waits while you type. Send your line, and the story follows it from where you stopped reading. The words you hadn't read yet are dropped. To speed the narrator up, <strong>press and hold the words</strong>.</>, Demo: DemoCut },
  { h: "Look closer at anything", p: <>The story remembers the people, places and things it names. Tap one of those names to read more about it; what you read can name more things to tap. New names are underlined, and older ones still open. <strong>Looking closer doesn't use a turn</strong>, and the story waits while you look. Under what you read, <strong>Pocket it</strong> keeps the name in your pocket, the row just above your line, next to what you carry. Tap it there to write with it.</>, Demo: DemoLook },
  { h: "The world answers back", p: <>The world doesn't simply go along with you. People refuse, bargain and lie, and things have a price. Want to know something your character can't be sure of? Start a line with <strong>/ask</strong>: “/ask Is the ferryman lying?” The answer comes inside the story, never as a plain yes or no. Want the story to move? <strong>/trouble</strong> makes something happen now, <strong>/clock</strong> sets a deadline of your own, and <strong>/flashback</strong> makes something you did earlier true. Type <strong>/</strong> to see every command.</>, Demo: DemoAsk },
  { h: PENS_STEP, p: <>Above your line are two pens. <strong>Ink</strong> is what you do, as in every line so far. <strong>Thread</strong> is what you make true: tap Thread, write “The ferryman is my cousin, and he owes me a favour,” and send. The narrator makes it so, and then the world pulls back with a price, in rust under the reply. You have <strong>three threads a chapter</strong>: the knots beside the pen. The world refuses a thread that would undo what happened, break its own rules, or decide what someone else feels, and gives it back. Tap <strong>?</strong> beside the pens for the details.</>, Demo: DemoPens },
  { h: "Loose ends come back", p: <>The story keeps a list of its loose ends, called <strong>threads</strong>: a debt, a stranger's name, a suitcase that sailed without you. Leave one alone and it fades. Tap it to bring it back: the story returns to it on your next line, usually with more at stake.</>, Demo: DemoThreads },
  { h: "Chapters, the journal, and the storyteller", p: <>Stories come in chapters. Each one asks a question, like “Will you reach Low Anchorage before the tide turns?”, and its ending says what it cost. Prefer a story that simply flows? Turn chapters off in the <strong>journal</strong>. To speak to the storyteller rather than as your character, just say so: “slower, please,” “no spiders.”</>, Demo: DemoChapters },
];
const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII"];

export function Onboarding({ skin, inStory, onDone, onList }: { skin: Skin; inStory: boolean; onDone(): void; onList(): void }) {
  const [i, setI] = useState(0);
  const last = i === STEPS.length - 1;
  const done = () => { markOnboarded(); onDone(); };
  const go = (d: number) => setI((c) => Math.max(0, Math.min(STEPS.length - 1, c + d)));
  const startX = useRef<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  // Over a story: focus moves here (typing mustn't reach the line underneath) and goes back when the tour closes.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus({ preventScroll: true });
    return () => prev?.focus?.({ preventScroll: true });
  }, []);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "Escape") done();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  const step = STEPS[i]!;
  // A player who has seen the pens here doesn't need V2's introduction above the line as well.
  useEffect(() => { if (step.h === PENS_STEP) introSeen(); }, [step.h]);
  return (
    <div className="lm-ob" role="dialog" aria-modal="true" aria-labelledby="lm-ob-h" ref={ref} tabIndex={-1}
      onPointerDown={(e) => { startX.current = e.pointerType === "touch" ? e.clientX : null; }}
      onPointerUp={(e) => { if (startX.current !== null) { const dx = e.clientX - startX.current; if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1); } startX.current = null; }}>
      <div className="lm-ob-in">
        <header className="lm-ob-top">
          <span className="lm-ob-mark">Weftwake</span>
          <ol className="lm-ob-threads" aria-label={`Step ${i + 1} of ${STEPS.length}`}>
            {STEPS.map((s, k) => (
              <li key={s.h} data-state={k < i ? "done" : k === i ? "now" : "next"}>
                <button type="button" onClick={() => setI(k)} aria-label={`Step ${k + 1}: ${s.h}`} aria-current={k === i ? "step" : undefined} />
              </li>
            ))}
          </ol>
          <button type="button" className="lm-ob-skip" onClick={done}>{inStory ? "Close" : "Skip"}</button>
        </header>
        <section key={i} className="lm-ob-step">
          <span className="lm-ob-num" aria-hidden="true">{ROMAN[i]}</span>
          <h1 id="lm-ob-h">{step.h}</h1>
          <p className="lm-ob-lede">{step.p}</p>
          <div className="lm-ob-stage" aria-label="An example">
            <step.Demo w={WORDS[skin]} />
          </div>
          {last && (
            <p className="lm-ob-after">Looking closer and opening your journal never use a turn. Type “help” any time for <button type="button" className="lm-ob-link" onClick={onList}>the short list</button>.</p>
          )}
        </section>
        <footer className="lm-ob-nav">
          {i > 0 ? <button type="button" className="lm-ob-back" onClick={() => go(-1)}>Back</button> : <span />}
          <button type="button" className="lm-cta lm-ob-next" onClick={() => (last ? done() : go(1))}>{last ? (inStory ? "Back to your story" : "Begin a story") : "Next"}</button>
        </footer>
        {i === 0 && <Credit className="lm-ob-credit" />}
      </div>
    </div>
  );
}

/**
 * The short list: everything a player can do, on one sheet ("help"). What uses a turn and what doesn't, the commands,
 * the storyteller, what's kept, chapters and endings, voices; the dream and turns lines follow the player's plan
 * (before payments open, a free player, a Patron).
 */
export function ShortList({ at, onTour, onClose }: { at?: string; onTour(): void; onClose(): void }) {
  const [v, setV] = useState<PatronView | null>(null);
  useEffect(() => { api.patron().then(setV).catch(() => {}); }, []);
  // Opened at a section (the pen switch's "?"): that section at the top of the sheet.
  useEffect(() => {
    if (!at) return;
    requestAnimationFrame(() => document.getElementById(`lm-short-${at}`)?.scrollIntoView({ block: "start" }));
  }, [at]);
  const firstDream = `Your first dream comes while you play: after ${FIRST_DREAM_AT} turns, or when your first chapter ends. The loom rereads the story so far, writes a short recap, sometimes draws a plate (a black-and-white picture of the chapter you can save), and quietly plans which loose ends will come back.`;
  const dreams = !v ? null
    : v.patron ? "Every night after you've played, the loom dreams over your story: a short recap, sometimes a plate (a black-and-white picture of the chapter you can save), and loose ends it means to bring back. You'll see it the next time you open the story."
    : !v.open ? `${firstDream} During the beta, it dreams again every night after you've played.`
    : `${firstDream} After that, Patrons get a dream every night they play.`;
  const turns = !v ? null
    : !v.open ? "During the beta, every turn is free."
    : v.patron ? `As a Patron you have ${PATRON_TURNS_PER_MONTH.toLocaleString()} turns a month, across all your stories. If you run out, packs of extra turns carry over until you use them.`
    : `You have ${FREE_TURNS} free turns, across all your stories. After that, become a Patron (${PATRON_TURNS_PER_MONTH.toLocaleString()} turns a month), or buy 60 turns to finish the chapter you're in.`;
  return (
    <Sheet label="How to play: the short list" onClose={onClose}>
      <div className="lm-short">
        <header className="lm-short-head">
          <h2 className="lm-sheet-h">How to play: the short list</h2>
          <button type="button" className="lm-short-tour" onClick={onTour}>Take the tour</button>
        </header>
        <dl>
          <dt>Uses a turn</dt>
          <dd>Every line you send: what your character does, every command (<code>/ask</code>, <code>/elsewhere</code>, <code>/trouble</code>, <code>/clock</code>, <code>/flashback</code>, <code>/weave</code>), bringing back a thread, and speaking to the storyteller. A command you can't use yet costs nothing. {turns}</dd>
          <dt>Never uses a turn</dt>
          <dd>Looking closer at a name, putting a name in your pocket, opening your journal, striking something you kept, asking to end the story, and everything under “Just type” below. The story waits while you do them.</dd>
          <dt>While the narrator writes</dt>
          <dd>Press and hold the words to speed it up. Start typing and it waits. Send, and the story follows your line from where you stopped reading.</dd>
          <dt id="lm-short-pens">The two pens: Ink and Thread</dt>
          <dd className="lm-short-paras">
            <p>Above your line are two pens. <strong>Ink</strong> is what you do or say, as always: “I knock on the ferryman's door.” The world answers, and it can say no for a reason the story holds.</p>
            <p><strong>Thread</strong> is what you make true. Tap Thread (your line turns gold), write a fact, and send: “The ferryman is my cousin, and he owes me a favour.” The narrator makes it true, and then the world pulls back: under the reply, in rust, it names the price (“You carry his sealed parcel across, and if customs stop you, it's yours”). The price becomes a loose end the story presses on. After each Thread line the pen goes back to Ink. Typing <code>/weave</code> before your words does the same.</p>
            <p><strong>How many:</strong> three a chapter, shown as the knots beside Thread. Between chapters, and in a story with chapters turned off, you have one at a time, and it comes back ten turns after you use it. With none left, a Thread line is turned away before it uses a turn. A Thread line uses a turn, like any line.</p>
            <p><strong>When the world says no:</strong> it won't hold a thread that undoes what already happened (“the vase was never broken”), breaks the world's own rules (a living doctor on an island where everyone is a zombie), or decides what someone else feels or chooses. The reply shows it almost holding, then not, and under it: “The world wouldn't hold that thread. It's yours again.” The knot comes back a few seconds after the reply.</p>
            <p><strong>Good threads</strong> are facts about you, your past or the place: a key in your coat, a cousin on the ferry, a back door left unlocked. (The Thread pen isn't the same as the story's loose ends, which your journal also calls threads.)</p>
          </dd>
          <dt id="lm-short-pocket">Your pocket</dt>
          <dd className="lm-short-paras">
            <p>Tap a name in the story to look closer at it, then <strong>Pocket it</strong>: the name goes in your pocket, the row of names just above your line, beside what you carry (marked ◦). Tap a name in your pocket and it goes into your line, with either pen: “I show Pavel <em>the brass key</em>.” <strong>Write with it</strong>, under a look closer, does the same straight away.</p>
            <p>A name does nothing on its own; it keeps what matters to you close, ready to use. If the story takes something away (someone else has it, it's broken, a place is out of reach), its name stays in your pocket struck through, and tapping it says why. Take a name out with the × beside it. Up to twelve names. Pocketing never uses a turn.</p>
          </dd>
          <dt>Commands</dt>
          <dd><code>/ask</code> a yes-or-no question the world answers inside the story (yes, no, yes but…, no and…).<br />
            <code>/elsewhere</code> a place or a time: the story jumps there, and time passes while you're away.<br />
            <code>/trouble</code> asks for trouble: something happens now, and the world chooses what. Once a chapter.<br />
            <code>/clock until</code> something (“/clock until dawn”) sets a clock of your own. It fills as time passes and the story pushes; something happens when it runs out. Two at most.<br />
            <code>/flashback</code> something your character did earlier (“/flashback I paid the ferryman last week”): it's true, and it helps with the chapter's question, but a clock moves on and the world adds a complication. Once a chapter.<br />
            <code>/weave</code> something true now (“/weave The ferryman owes me a crossing”): the same as a Thread line. See “The two pens” above.<br />
            <code>/journal</code> your journal: the threads you can bring back, places you've been, what you've kept, the chapters, and find.</dd>
          <dt>Set the scene</dt>
          <dd>When you begin a new story, “Set the scene (optional)” under your first line takes up to three things that are true here (“The ferryman is my brother”) and one thing you're afraid of. The world keeps what's true from the first line on. Your fear stays with the storyteller: it shapes what's at stake and is never named.</dd>
          <dt>Pass it on</dt>
          <dd>When a chapter ends, or the story does, “Pass this opening on” makes a private link: someone else begins their own story from your first line, its truths and its narrator. Neither of you sees a word of the other's story, and your fear stays yours. As chapters finish, you each see the other's chapter cards (the question, how it ended, the price) in the journal's Chapters tab.</dd>
          <dt>Your stories</dt>
          <dd>Stories are saved as you go, so you can leave any time and pick up where you left off. Tap “Weftwake ▾” at the top (or “stories” at the bottom, or type “stories”) to switch to another story or begin a new one.</dd>
          <dt>The map</dt>
          <dd>Your journal's Places tab draws where you've been, in the order you went, with a ring where you are. Places you haven't been back to fade the way memory does, and a place never moves once it's on the map. Under the map, “How to read the map” says what every mark means. Tap a place to look closer or go back there. The ◎ chip above where you write shows where you are now: what's here, what you carry, and the map.</dd>
          <dt>Kept</dt>
          <dd>When the story lets a fact from your line stand (“I carry my father's watch”), your line is marked <em>kept</em> and the fact stays true. If it clashes with the world, the world wins.</dd>
          <dt>The storyteller</dt>
          <dd>Speak to it rather than as your character, in plain words: “slower, please”, “no spiders”, “keep it short”. It keeps to what you ask; your journal lists it under Kept, where you can strike it.</dd>
          <dt>Chapters and endings</dt>
          <dd>Each chapter asks a question. A moment of decision comes, and the chapter ends Earned, Answered at a price, or Lost. Turn chapters off in the journal and the story simply flows. Type “end the story” when you're ready: the ending comes within a few turns, and you can stay in the world after it.</dd>
          <dt>Narrator</dt>
          <dd>Four voices (Inward, Spare, Fable, Noir). Weftwake picks one from your first line; change it in the journal, or set one for new stories in Account settings.</dd>
          {dreams && <><dt>Dreams and plates</dt><dd>{dreams}</dd></>}
          <dt id="lm-short-away">While you were away</dt>
          <dd>When the loom dreams over your story at night, the world also moves on without you: two to five small things happen out of your sight, each grown from what the story already holds (what someone wants, a deadline that was running, a loose end you left). You'll find them under “While you were away” when you come back, and a line of them in your morning letter if you get one. They're true from then on, and the story picks them up with your next line. Time counts too: a deadline the story set that fell while you were away (the 21:40 ferry) has passed. Nothing is decided for you: no one in the story dies or leaves it, no question is answered, and nothing happens to what you carry.</dd>
          <dt>Just type</dt>
          <dd>“help” (this list), “how to play” (the tour), “stories”, “journal”, “cloth” (your story as woven cloth), “account”, “feedback”, “privacy”, “safety”, “export my data”, “delete everything”.</dd>
        </dl>
        <button type="button" onClick={onClose}>Close</button>
      </div>
    </Sheet>
  );
}
